import { inject, Injectable } from '@angular/core';
import {
	doc,
	docData,
	Firestore,
	getDoc,
} from '@marmon/frontend/firebase/firestore';
import {
	ConfigDocumentIds,
	ConfigDocumentMap,
	InsightsConfig,
	ReportsConfig,
	SupportConfig,
	TimeConfig,
} from '@marmon/shared/types/config';
import { map, Observable } from 'rxjs';

const CONFIG_COLLECTION = 'config';

@Injectable({ providedIn: 'root' })
export class ConfigApiService {
	private readonly firestore = inject(Firestore);

	/**
	 * Fetches a config document by ID. Returns null if the document does not exist.
	 */
	getConfig<K extends keyof ConfigDocumentMap>(
		documentId: K,
	): Promise<ConfigDocumentMap[K] | null> {
		return getDoc(
			doc(this.firestore, CONFIG_COLLECTION, documentId as string),
		).then((snapshot) =>
			snapshot.exists()
				? (snapshot.data() as ConfigDocumentMap[K])
				: null,
		);
	}

	/**
	 * Observable stream of a config document. Emits when the document changes.
	 */
	getConfig$<K extends keyof ConfigDocumentMap>(
		documentId: K,
	): Observable<ConfigDocumentMap[K] | null> {
		return docData(
			doc(this.firestore, CONFIG_COLLECTION, documentId as string),
		) as Observable<ConfigDocumentMap[K] | null>;
	}

	/**
	 * Fetches the reports config. Returns null if the document does not exist.
	 */
	getReportsConfig(): Promise<ReportsConfig | null> {
		return this.getConfig(ConfigDocumentIds.Reports);
	}

	/**
	 * Observable stream of reports config. Emits when the document changes.
	 */
	getReportsConfig$(): Observable<ReportsConfig | null> {
		return this.getConfig$(ConfigDocumentIds.Reports);
	}

	/**
	 * Fetches the support config. Returns null if the document does not exist.
	 */
	getSupportConfig(): Promise<SupportConfig | null> {
		return this.getConfig(ConfigDocumentIds.Support);
	}

	/**
	 * Observable stream of support config. Emits when the document changes.
	 */
	getSupportConfig$(): Observable<SupportConfig | null> {
		return this.getConfig$(ConfigDocumentIds.Support);
	}

	/**
	 * Fetches the support email. Returns null if not configured.
	 */
	getSupportEmail(): Promise<string | null> {
		return this.getSupportConfig().then((config) => config?.email ?? null);
	}

	/**
	 * Observable stream of the support email. Emits when the config changes.
	 */
	getSupportEmail$(): Observable<string | null> {
		return this.getSupportConfig$().pipe(
			map((config) => config?.email ?? null),
		);
	}

	/**
	 * Fetches the time config (quarter boundaries). Returns null if the document does not exist.
	 */
	getTimeConfig(): Promise<TimeConfig | null> {
		return this.getConfig(ConfigDocumentIds.Time);
	}

	/**
	 * Observable stream of time config. Emits when the document changes.
	 */
	getTimeConfig$(): Observable<TimeConfig | null> {
		return this.getConfig$(ConfigDocumentIds.Time);
	}

	/**
	 * Insight feature flag and poll stats. Returns null if the document
	 * does not exist, which the app treats as disabled.
	 */
	getInsightsConfig(): Promise<InsightsConfig | null> {
		return this.getConfig(ConfigDocumentIds.Insights);
	}

	/**
	 * Observable stream of insight config. Emits when the document changes.
	 */
	getInsightsConfig$(): Observable<InsightsConfig | null> {
		return this.getConfig$(ConfigDocumentIds.Insights);
	}
}
